/**
 * Edit-mode gsplat shader chunks (GLSL for WebGL2, WGSL for WebGPU).
 *
 * They replace the engine's gsplatVS / gsplatPS / gsplatCenterVS on the edit
 * entity's own (non-unified) material, following PlayCanvas 2.23's chunks and
 * adding:
 *  - per-splat state (selected / hidden / deleted, plus the display-only
 *    "marked" preview tint) from an R8 texture,
 *  - per-splat transform palette (center chunk) plus a live preview transform
 *    applied to the selection while a gizmo is dragged,
 *  - per-splat colour grade palette plus a live preview grade on the selection,
 *  - SuperSplat display aids: selection / unselected tints, hidden ("locked")
 *    colour, rings, center dots, gaussians on/off,
 *  - a pick pass that writes the splat index, with op-aware filtering
 *    (add skips selected splats, remove only sees selected ones).
 *
 * Palettes: transform entries are 3 RGBA32F texels (rows of a 3x4 matrix),
 * colour entries 4 texels (3 grade rows + transparency), PALETTE_ROW entries
 * per texture row.
 *
 * initCenter must fill every SplatCenter field the engine's corner chunk reads:
 * PlayCanvas 2.23 added `projMat11` (its focal length and Y sign), and a
 * center that leaves it unset collapses every gaussian to nothing. The
 * engine's GSPLAT_FISHEYE branch is left out: fisheye is only applied to the
 * unified splats (scene.gsplat.fisheye), never to this non-unified material.
 */

export const PALETTE_ROW = 1024;

/** Pick filter modes (uniform pickOp). */
export const PICK_OP_ALL = 0;
export const PICK_OP_UNSELECTED = 1;
export const PICK_OP_SELECTED = 2;

// ─── GLSL ───────────────────────────────────────────────────

export const centerGLSL = /* glsl */`
uniform highp usampler2D splatTransformIndex;
uniform highp sampler2D transformPalette;
uniform sampler2D splatState;
uniform mat4 previewTransform;
uniform float previewTransformEnabled;

mat4 readPaletteMatrix(uint index) {
    int u = int(index % ${PALETTE_ROW}u) * 3;
    int v = int(index / ${PALETTE_ROW}u);
    mat4 t;
    t[0] = texelFetch(transformPalette, ivec2(u, v), 0);
    t[1] = texelFetch(transformPalette, ivec2(u + 1, v), 0);
    t[2] = texelFetch(transformPalette, ivec2(u + 2, v), 0);
    t[3] = vec4(0.0, 0.0, 0.0, 1.0);
    return transpose(t);
}

mat4 editModelMatrix(mat4 model) {
    uint transformIndex = texelFetch(splatTransformIndex, splat.uv, 0).r;
    mat4 m = transformIndex == 0u ? model : model * readPaletteMatrix(transformIndex);
    if (previewTransformEnabled > 0.5) {
        // 1: selection only, 2: every live splat
        uint s = uint(texelFetch(splatState, splat.uv, 0).r * 255.0 + 0.5);
        if (previewTransformEnabled > 1.5 || (s & 7u) == 1u) {
            m = model * previewTransform * (transformIndex == 0u ? mat4(1.0) : readPaletteMatrix(transformIndex));
        }
    }
    return m;
}

uniform mat4 matrix_model;
uniform mat4 matrix_view;
#ifndef GSPLAT_CENTER_NOPROJ
    uniform vec4 camera_params;
    uniform mat4 matrix_projection;
#endif

bool initCenter(vec3 modelCenter, inout SplatCenter center) {
    mat4 modelView = matrix_view * editModelMatrix(matrix_model);
    vec4 centerView = modelView * vec4(modelCenter, 1.0);

    #ifndef GSPLAT_CENTER_NOPROJ
        if (camera_params.w != 1.0 && centerView.z > 0.0) {
            return false;
        }
        vec4 centerProj = matrix_projection * centerView;
        #if WEBGPU
            centerProj.z = clamp(centerProj.z, 0.0, abs(centerProj.w));
        #else
            centerProj.z = clamp(centerProj.z, -abs(centerProj.w), abs(centerProj.w));
        #endif
        center.proj = centerProj;
        center.projMat00 = matrix_projection[0][0];
        center.projMat11 = matrix_projection[1][1];
    #endif

    center.view = centerView.xyz / centerView.w;
    center.modelView = modelView;
    return true;
}
`;

export const vertexGLSL = /* glsl */`
#include "gsplatCommonVS"

uniform highp usampler2D splatColorIndex;
uniform highp sampler2D colorPalette;

uniform vec4 selectedClr;       // rgb, blend
uniform vec4 unselectedClr;     // rgb, blend
uniform vec4 lockedClr;         // rgb, alpha
uniform vec4 displayFlags;      // x: gaussians (centers: 2 = selected only), y: centers mode, z: center size px, w: ring size (< 0: selected only)
uniform vec4 previewGrade0;
uniform vec4 previewGrade1;
uniform vec4 previewGrade2;
uniform vec2 previewGradeAlpha; // x: transparency, y: 0 off, 1 selection, 2 everything

varying mediump vec2 gaussianUV;
varying mediump vec4 gaussianColor;
varying mediump vec2 editFlags; // x: selected, y: hidden

#ifdef PICK_PASS
    uniform uint pickOp;
#endif

mediump vec4 discardVec = vec4(0.0, 0.0, 2.0, 1.0);

vec4 readSplatColor() {
    #ifdef GSPLAT_SEPARATE_OPACITY
        float opacity = getOpacity();
        return vec4(getColor(), opacity);
    #else
        return getColor();
    #endif
}

vec4 applyGradeRows(vec4 c, vec4 r0, vec4 r1, vec4 r2, float transparency) {
    vec3 rgb = vec3(dot(r0.xyz, c.xyz) + r0.w, dot(r1.xyz, c.xyz) + r1.w, dot(r2.xyz, c.xyz) + r2.w);
    return vec4(rgb, c.a * transparency);
}

void main(void) {
    SplatSource source;
    if (!initSource(source)) {
        gl_Position = discardVec;
        return;
    }

    uint rawState = uint(texelFetch(splatState, splat.uv, 0).r * 255.0 + 0.5);
    uint state = rawState & 7u;
    bool selected = (state & 1u) != 0u;
    bool hidden = (state & 2u) != 0u;
    bool marked = (rawState & 8u) != 0u;
    // deleted, or painted over by a replace clone stroke in progress
    if ((state & 4u) != 0u || (rawState & 16u) != 0u) {
        gl_Position = discardVec;
        return;
    }

    bool centersMode = displayFlags.y > 0.5;

    #ifdef PICK_PASS
        if (hidden || (pickOp == ${PICK_OP_UNSELECTED}u && selected) || (pickOp == ${PICK_OP_SELECTED}u && !selected)) {
            gl_Position = discardVec;
            return;
        }
    #else
        if (!centersMode && displayFlags.x < 0.5) {
            gl_Position = discardVec;
            return;
        }
        if (centersMode && (hidden || (displayFlags.x > 1.5 && !selected))) {
            gl_Position = discardVec;
            return;
        }
        // outline mask: only the selection
        if (displayFlags.x > 2.5 && (!selected || hidden)) {
            gl_Position = discardVec;
            return;
        }
    #endif

    vec3 modelCenter = getCenter();
    SplatCenter center;
    center.modelCenterOriginal = modelCenter;
    center.modelCenterModified = modelCenter;
    if (!initCenter(modelCenter, center)) {
        gl_Position = discardVec;
        return;
    }

    SplatCorner corner;
    if (centersMode) {
        vec2 c = center.proj.ww * viewport_size.zw;
        corner.offset = vec3(source.cornerUV * displayFlags.z * c, 0.0);
        corner.uv = source.cornerUV;
    } else if (!initCorner(source, center, corner)) {
        gl_Position = discardVec;
        return;
    }

    vec4 clr = readSplatColor();
    #if GSPLAT_AA
        if (!centersMode) clr.a *= corner.aaFactor;
    #endif

    #if SH_BANDS > 0
        vec3 dir = normalize(center.view * mat3(center.modelView));
        vec3 sh[SH_COEFFS];
        float scale;
        readSHData(sh, scale);
        clr.xyz += evalSH(sh, dir) * scale;
    #endif

    uint colorIndex = texelFetch(splatColorIndex, splat.uv, 0).r;
    if (colorIndex != 0u) {
        int u = int(colorIndex % ${PALETTE_ROW}u) * 4;
        int v = int(colorIndex / ${PALETTE_ROW}u);
        clr = applyGradeRows(clr,
            texelFetch(colorPalette, ivec2(u, v), 0),
            texelFetch(colorPalette, ivec2(u + 1, v), 0),
            texelFetch(colorPalette, ivec2(u + 2, v), 0),
            texelFetch(colorPalette, ivec2(u + 3, v), 0).x);
    }
    if (previewGradeAlpha.y > 1.5 || (selected && previewGradeAlpha.y > 0.5)) {
        clr = applyGradeRows(clr, previewGrade0, previewGrade1, previewGrade2, previewGradeAlpha.x);
    }
    clr.a = clamp(clr.a, 0.0, 1.0);

    #ifdef PICK_PASS
        float alphaClipValue = alphaClip;
    #else
        float alphaClipValue = alphaClipForward;
    #endif
    if (!centersMode) {
        if (clr.a <= alphaClipValue) {
            gl_Position = discardVec;
            return;
        }
        clipCorner(corner, clr.a);
    }

    #if GSPLAT_2DGS
        if (!centersMode) {
            vec3 modelCorner = center.modelCenterModified + corner.offset;
            gl_Position = matrix_projection * center.modelView * vec4(modelCorner, 1.0);
        } else {
            gl_Position = center.proj + vec4(corner.offset.xyz, 0.0);
        }
    #else
        gl_Position = center.proj + vec4(corner.offset.xyz, 0.0);
    #endif
    gaussianUV = corner.uv;
    editFlags = vec2(selected ? 1.0 : 0.0, hidden ? 1.0 : 0.0);

    #ifdef PICK_PASS
        // pick pass: x carries opacity so the fragment test matches what is visible
        editFlags = vec2(clr.a, 0.0);
        uvec4 bits = (uvec4(splat.index) >> uvec4(0u, 8u, 16u, 24u)) & uvec4(255u);
        gaussianColor = vec4(bits) / 255.0;
    #else
        vec3 rgb = prepareOutputFromGamma(max(clr.xyz, 0.0), -center.view.z);
        if (displayFlags.x > 2.5) {
            rgb = vec3(1.0);
        } else if (centersMode) {
            rgb = selected ? selectedClr.xyz : unselectedClr.xyz;
            clr.a = 1.0;
        } else if (hidden) {
            rgb = mix(rgb, lockedClr.xyz, 0.5);
            clr.a *= lockedClr.a;
        } else if (marked) {
            // preview of what an action will remove
            rgb = mix(rgb, vec3(1.0, 0.18, 0.12), 0.75);
            clr.a = max(clr.a, 0.35);
        } else if (selected) {
            rgb = mix(rgb, selectedClr.xyz, selectedClr.w);
        } else {
            rgb = mix(rgb, unselectedClr.xyz, unselectedClr.w);
        }
        gaussianColor = vec4(rgb, clr.a);
    #endif
}
`;

export const fragmentGLSL = /* glsl */`
#if defined(PICK_PASS)
    uniform float alphaClip;
#else
    uniform float alphaClipForward;
#endif
#ifdef SPLAT_EDIT_DEPTH
    uniform float splatEditDepthClip;
#endif
uniform vec4 displayFlags;

varying mediump vec2 gaussianUV;
varying mediump vec4 gaussianColor;
varying mediump vec2 editFlags;

const float EXP4 = exp(-4.0);
const float INV_EXP4 = 1.0 / (1.0 - EXP4);

float normExp(float x) {
    return (exp(x * -4.0) - EXP4) * INV_EXP4;
}

void main(void) {
    mediump float A = dot(gaussianUV, gaussianUV);
    if (A > 1.0) {
        discard;
    }

    #ifdef PICK_PASS
        if (normExp(A) * editFlags.x < alphaClip) {
            discard;
        }
        gl_FragColor = gaussianColor;
    #elif defined(SPLAT_EDIT_DEPTH)
        // depth only (colour writes are off): where this splat is solid enough to hide what is behind it
        if (normExp(A) * gaussianColor.a < splatEditDepthClip) {
            discard;
        }
        gl_FragColor = vec4(0.0);
    #else
        if (displayFlags.y > 0.5) {
            // center dot: solid disc
            gl_FragColor = vec4(gaussianColor.xyz, 1.0);
            return;
        }
        mediump float alpha = normExp(A) * gaussianColor.a;
        // rings: positive size outlines every gaussian, negative only the selected ones
        float ringSize = displayFlags.w > 0.0 || editFlags.x > 0.5 ? abs(displayFlags.w) : 0.0;
        if (ringSize > 0.0 && editFlags.y < 0.5) {
            // rings: emphasise each gaussian's outline
            alpha = A < 1.0 - ringSize ? max(0.04, alpha * 0.5) : max(alpha, 0.6);
        }
        if (alpha < alphaClipForward) {
            discard;
        }
        gl_FragColor = vec4(gaussianColor.xyz * alpha, alpha);
    #endif
}
`;

// ─── WGSL ───────────────────────────────────────────────────

export const centerWGSL = /* wgsl */`
var splatTransformIndex: texture_2d<u32>;
var transformPalette: texture_2d<f32>;
var splatState: texture_2d<f32>;
uniform previewTransform: mat4x4f;
uniform previewTransformEnabled: f32;

fn readPaletteMatrix(index: u32) -> mat4x4f {
    let u: i32 = i32(index % ${PALETTE_ROW}u) * 3;
    let v: i32 = i32(index / ${PALETTE_ROW}u);
    let t: mat4x4f = mat4x4f(
        textureLoad(transformPalette, vec2i(u, v), 0),
        textureLoad(transformPalette, vec2i(u + 1, v), 0),
        textureLoad(transformPalette, vec2i(u + 2, v), 0),
        vec4f(0.0, 0.0, 0.0, 1.0)
    );
    return transpose(t);
}

fn editModelMatrix(model: mat4x4f) -> mat4x4f {
    let transformIndex: u32 = textureLoad(splatTransformIndex, splat.uv, 0).r;
    var palette: mat4x4f = mat4x4f(
        vec4f(1.0, 0.0, 0.0, 0.0), vec4f(0.0, 1.0, 0.0, 0.0),
        vec4f(0.0, 0.0, 1.0, 0.0), vec4f(0.0, 0.0, 0.0, 1.0)
    );
    if (transformIndex != 0u) {
        palette = readPaletteMatrix(transformIndex);
    }
    if (uniform.previewTransformEnabled > 0.5) {
        // 1: selection only, 2: every live splat
        let s: u32 = u32(textureLoad(splatState, splat.uv, 0).r * 255.0 + 0.5);
        if (uniform.previewTransformEnabled > 1.5 || (s & 7u) == 1u) {
            return model * uniform.previewTransform * palette;
        }
    }
    return model * palette;
}

uniform matrix_model: mat4x4f;
uniform matrix_view: mat4x4f;
#ifndef GSPLAT_CENTER_NOPROJ
    uniform camera_params: vec4f;
    uniform matrix_projection: mat4x4f;
#endif

fn initCenter(modelCenter: vec3f, center: ptr<function, SplatCenter>) -> bool {
    let modelView: mat4x4f = uniform.matrix_view * editModelMatrix(uniform.matrix_model);
    let centerView: vec4f = modelView * vec4f(modelCenter, 1.0);

    #ifndef GSPLAT_CENTER_NOPROJ
        if (uniform.camera_params.w != 1.0 && centerView.z > 0.0) {
            return false;
        }
        var centerProj: vec4f = uniform.matrix_projection * centerView;
        centerProj.z = clamp(centerProj.z, 0.0, abs(centerProj.w));
        center.proj = centerProj;
        center.projMat00 = uniform.matrix_projection[0][0];
        center.projMat11 = uniform.matrix_projection[1][1];
    #endif

    center.view = centerView.xyz / centerView.w;
    center.modelView = modelView;
    return true;
}
`;

export const vertexWGSL = /* wgsl */`
#include "gsplatCommonVS"

var splatColorIndex: texture_2d<u32>;
var colorPalette: texture_2d<f32>;

uniform selectedClr: vec4f;
uniform unselectedClr: vec4f;
uniform lockedClr: vec4f;
uniform displayFlags: vec4f;
uniform previewGrade0: vec4f;
uniform previewGrade1: vec4f;
uniform previewGrade2: vec4f;
uniform previewGradeAlpha: vec2f;

varying gaussianUV: vec2f;
varying gaussianColor: vec4f;
varying editFlags: vec2f;

#ifdef PICK_PASS
    uniform pickOp: u32;
#endif

const discardVec: vec4f = vec4f(0.0, 0.0, 2.0, 1.0);

fn readSplatColor() -> vec4f {
    #ifdef GSPLAT_SEPARATE_OPACITY
        let opacity: f32 = f32(getOpacity());
        return vec4f(vec3f(getColor()), opacity);
    #else
        return vec4f(getColor());
    #endif
}

fn applyGradeRows(c: vec4f, r0: vec4f, r1: vec4f, r2: vec4f, transparency: f32) -> vec4f {
    let rgb: vec3f = vec3f(dot(r0.xyz, c.xyz) + r0.w, dot(r1.xyz, c.xyz) + r1.w, dot(r2.xyz, c.xyz) + r2.w);
    return vec4f(rgb, c.a * transparency);
}

@vertex
fn vertexMain(input: VertexInput) -> VertexOutput {
    var output: VertexOutput;

    var source: SplatSource;
    if (!initSource(&source)) {
        output.position = discardVec;
        return output;
    }

    let rawState: u32 = u32(textureLoad(splatState, splat.uv, 0).r * 255.0 + 0.5);
    let state: u32 = rawState & 7u;
    let selected: bool = (state & 1u) != 0u;
    let hidden: bool = (state & 2u) != 0u;
    let marked: bool = (rawState & 8u) != 0u;
    // deleted, or painted over by a replace clone stroke in progress
    if ((state & 4u) != 0u || (rawState & 16u) != 0u) {
        output.position = discardVec;
        return output;
    }

    let centersMode: bool = uniform.displayFlags.y > 0.5;

    #ifdef PICK_PASS
        if (hidden || (uniform.pickOp == ${PICK_OP_UNSELECTED}u && selected) || (uniform.pickOp == ${PICK_OP_SELECTED}u && !selected)) {
            output.position = discardVec;
            return output;
        }
    #else
        if (!centersMode && uniform.displayFlags.x < 0.5) {
            output.position = discardVec;
            return output;
        }
        if (centersMode && (hidden || (uniform.displayFlags.x > 1.5 && !selected))) {
            output.position = discardVec;
            return output;
        }
        // outline mask: only the selection
        if (uniform.displayFlags.x > 2.5 && (!selected || hidden)) {
            output.position = discardVec;
            return output;
        }
    #endif

    var modelCenter: vec3f = getCenter();
    var center: SplatCenter;
    center.modelCenterOriginal = modelCenter;
    center.modelCenterModified = modelCenter;
    if (!initCenter(modelCenter, &center)) {
        output.position = discardVec;
        return output;
    }

    var corner: SplatCorner;
    if (centersMode) {
        let c: vec2f = center.proj.ww * uniform.viewport_size.zw;
        corner.offset = vec3f(vec2f(source.cornerUV) * uniform.displayFlags.z * c, 0.0);
        corner.uv = source.cornerUV;
    } else if (!initCorner(&source, &center, &corner)) {
        output.position = discardVec;
        return output;
    }

    var clr: vec4f = readSplatColor();
    #if GSPLAT_AA
        if (!centersMode) {
            clr.a = clr.a * f32(corner.aaFactor);
        }
    #endif

    #if SH_BANDS > 0
        let modelView3x3: mat3x3f = mat3x3f(center.modelView[0].xyz, center.modelView[1].xyz, center.modelView[2].xyz);
        let dir: vec3f = normalize(center.view * modelView3x3);
        var sh: array<half3, SH_COEFFS>;
        var scale: f32;
        readSHData(&sh, &scale);
        clr = vec4f(clr.xyz + vec3f(evalSH(&sh, dir)) * scale, clr.a);
    #endif

    let colorIndex: u32 = textureLoad(splatColorIndex, splat.uv, 0).r;
    if (colorIndex != 0u) {
        let u: i32 = i32(colorIndex % ${PALETTE_ROW}u) * 4;
        let v: i32 = i32(colorIndex / ${PALETTE_ROW}u);
        clr = applyGradeRows(clr,
            textureLoad(colorPalette, vec2i(u, v), 0),
            textureLoad(colorPalette, vec2i(u + 1, v), 0),
            textureLoad(colorPalette, vec2i(u + 2, v), 0),
            textureLoad(colorPalette, vec2i(u + 3, v), 0).x);
    }
    if (uniform.previewGradeAlpha.y > 1.5 || (selected && uniform.previewGradeAlpha.y > 0.5)) {
        clr = applyGradeRows(clr, uniform.previewGrade0, uniform.previewGrade1, uniform.previewGrade2, uniform.previewGradeAlpha.x);
    }
    clr.a = clamp(clr.a, 0.0, 1.0);

    #ifdef PICK_PASS
        let alphaClipValue: f32 = uniform.alphaClip;
    #else
        let alphaClipValue: f32 = uniform.alphaClipForward;
    #endif
    if (!centersMode) {
        if (clr.a <= alphaClipValue) {
            output.position = discardVec;
            return output;
        }
        clipCorner(&corner, half(clr.a));
    }

    output.position = center.proj + vec4f(vec3f(corner.offset), 0.0);
    output.gaussianUV = vec2f(corner.uv);
    output.editFlags = vec2f(select(0.0, 1.0, selected), select(0.0, 1.0, hidden));

    #ifdef PICK_PASS
        // pick pass: x carries opacity so the fragment test matches what is visible
        output.editFlags = vec2f(clr.a, 0.0);
        let bits: vec4u = (vec4u(splat.index) >> vec4u(0u, 8u, 16u, 24u)) & vec4u(255u);
        output.gaussianColor = vec4f(bits) / 255.0;
    #else
        var rgb: vec3f = prepareOutputFromGamma(max(clr.xyz, vec3f(0.0)), -center.view.z);
        var alpha: f32 = clr.a;
        if (uniform.displayFlags.x > 2.5) {
            rgb = vec3f(1.0);
        } else if (centersMode) {
            rgb = select(uniform.unselectedClr.xyz, uniform.selectedClr.xyz, selected);
            alpha = 1.0;
        } else if (hidden) {
            rgb = mix(rgb, uniform.lockedClr.xyz, 0.5);
            alpha = alpha * uniform.lockedClr.w;
        } else if (marked) {
            // preview of what an action will remove
            rgb = mix(rgb, vec3f(1.0, 0.18, 0.12), 0.75);
            alpha = max(alpha, 0.35);
        } else if (selected) {
            rgb = mix(rgb, uniform.selectedClr.xyz, uniform.selectedClr.w);
        } else {
            rgb = mix(rgb, uniform.unselectedClr.xyz, uniform.unselectedClr.w);
        }
        output.gaussianColor = vec4f(rgb, alpha);
    #endif
    return output;
}
`;

export const fragmentWGSL = /* wgsl */`
#ifdef PICK_PASS
    uniform alphaClip: f32;
#else
    uniform alphaClipForward: f32;
#endif
#ifdef SPLAT_EDIT_DEPTH
    uniform splatEditDepthClip: f32;
#endif
uniform displayFlags: vec4f;

varying gaussianUV: vec2f;
varying gaussianColor: vec4f;
varying editFlags: vec2f;

const EXP4: f32 = exp(-4.0);
const INV_EXP4: f32 = 1.0 / (1.0 - EXP4);

fn normExp(x: f32) -> f32 {
    return (exp(x * -4.0) - EXP4) * INV_EXP4;
}

@fragment
fn fragmentMain(input: FragmentInput) -> FragmentOutput {
    var output: FragmentOutput;
    let A: f32 = dot(gaussianUV, gaussianUV);
    if (A > 1.0) {
        discard;
        return output;
    }

    #ifdef PICK_PASS
        if (normExp(A) * editFlags.x < uniform.alphaClip) {
            discard;
            return output;
        }
        output.color = gaussianColor;
    #elif defined(SPLAT_EDIT_DEPTH)
        // depth only (colour writes are off): where this splat is solid enough to hide what is behind it
        if (normExp(A) * gaussianColor.a < uniform.splatEditDepthClip) {
            discard;
            return output;
        }
        output.color = vec4f(0.0);
    #else
        if (uniform.displayFlags.y > 0.5) {
            output.color = vec4f(gaussianColor.xyz, 1.0);
            return output;
        }
        var alpha: f32 = normExp(A) * gaussianColor.a;
        // rings: positive size outlines every gaussian, negative only the selected ones
        var ringSize: f32 = 0.0;
        if (uniform.displayFlags.w > 0.0 || editFlags.x > 0.5) {
            ringSize = abs(uniform.displayFlags.w);
        }
        if (ringSize > 0.0 && editFlags.y < 0.5) {
            if (A < 1.0 - ringSize) {
                alpha = max(0.04, alpha * 0.5);
            } else {
                alpha = max(alpha, 0.6);
            }
        }
        if (alpha < uniform.alphaClipForward) {
            discard;
            return output;
        }
        output.color = vec4f(gaussianColor.xyz * alpha, alpha);
    #endif
    return output;
}
`;
